import { useEffect, useState } from 'react'
import { clock, dolumEtiketi, price as fmtPrice, qty } from '../format'
import { fetchBotReport, type BotEvent, type BotReport } from '../ledger'

/**
 * Bot raporu — `Bot Hareketleri` satırındaki ⓘ açar.
 *
 * Amaç: botun NEYİ NEDEN yaptığını okumak. Zaman çizelgesi üç kaynağı
 * birleştirir: karar olayları (sebep + karar anındaki tahta özeti — çekirdek
 * her karar değişiminde yazar), emir yaşam döngüsü (açılış/düzenleme/iptal)
 * ve dolumlar (kendi tahta bağlamlarıyla). Her satırın ham yükü `ayrıntı`
 * ile açılır — derinlik seviyeleri, değerlendirilen parametreler orada.
 */

interface Props {
  botId: string
  onClose: () => void
}

/** category/action → rozet etiketi + ton sınıfı. */
function rozet(e: BotEvent): { ad: string; ton: string } {
  if (e.category === 'bot') {
    switch (e.action) {
      case 'decision': return { ad: 'Karar', ton: 'karar' }
      case 'ghost_shot': return { ad: 'Avcı', ton: 'avci' }
      case 'stop_loss': return { ad: 'Stop', ton: 'stop' }
      case 'param_error':
      case 'turn_error': return { ad: 'Hata', ton: 'hata' }
      default: return { ad: 'Yaşam', ton: 'yasam' }   // created/updated/paused...
    }
  }
  switch (e.action) {
    case 'filled': return { ad: 'Dolum', ton: 'dolum' }
    case 'created': return { ad: 'Emir', ton: 'emir' }
    case 'amended':
    case 'clipped': return { ad: 'Düzenleme', ton: 'emir' }
    case 'cancelled':
    case 'expired': return { ad: 'Kapanış', ton: 'yasam' }
    default: return { ad: e.action, ton: 'yasam' }
  }
}

/** Karar yükünden tek satırlık piyasa özeti — "o sırada tahta neredeydi". */
function baglamOzeti(p: Record<string, unknown>): string | null {
  const n = (v: unknown) => (typeof v === 'number' ? v : null)
  const parcalar: string[] = []
  const bid = n(p.bestBid)
  const ask = n(p.bestAsk)
  if (bid != null || ask != null) {
    parcalar.push(`tahta ${bid != null ? fmtPrice(bid) : '–'} / ${ask != null ? fmtPrice(ask) : '–'}`)
  }
  const net = n(p.net)
  if (net != null) parcalar.push(`net ${qty(net)}`)
  const alH = n(p.alHedef)
  const satH = n(p.satHedef)
  if (alH != null || satH != null) {
    parcalar.push(`hedef A ${alH != null ? fmtPrice(alH) : '–'} · S ${satH != null ? fmtPrice(satH) : '–'}`)
  }
  const son = p.lastTrade as { price?: number } | null | undefined
  if (son?.price != null) parcalar.push(`son işlem ${fmtPrice(son.price)}`)
  return parcalar.length ? parcalar.join('  ·  ') : null
}

const DURUM_AD: Record<string, string> = {
  RUNNING: 'Çalışıyor', PAUSED: 'Duraklatıldı', REMOVED: 'Kaldırıldı',
}

export function BotReportModal({ botId, onClose }: Props) {
  const [rapor, setRapor] = useState<BotReport | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [paramAcik, setParamAcik] = useState(false)

  useEffect(() => {
    let iptal = false
    fetchBotReport(botId)
      .then((r) => !iptal && setRapor(r))
      .catch((err) => !iptal && setHata(err instanceof Error ? err.message : 'Rapor alınamadı'))
    return () => {
      iptal = true
    }
  }, [botId])

  useEffect(() => {
    const kapat = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', kapat)
    return () => window.removeEventListener('keydown', kapat)
  }, [onClose])

  const b = rapor?.bot

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="order-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${botId} bot raporu`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <span className="modal-title">
            <code>{botId}</code>
            {b && (
              <span className="muted">
                {' · '}{b.type} · {b.contract}
              </span>
            )}
          </span>
          {/* Kontratı kapanan bot burada da "Çalışıyor" demez — listeyle aynı kural. */}
          {b && (
            <span className={`brapor-durum ${b.expired && b.status !== 'REMOVED' ? 'EXPIRED' : b.status}`}>
              {b.expired && b.status !== 'REMOVED' ? 'Kontrat kapandı' : DURUM_AD[b.status] ?? b.status}
            </span>
          )}
          <button type="button" className="modal-close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="modal-body">
          {hata && <div className="order-error">{hata}</div>}
          {!rapor && !hata && <div className="shadow-empty">Yükleniyor…</div>}

          {rapor && b && (
            <>
              <div className="ozet">
                <div className="ozet-kutu">
                  <span>Alış</span>
                  <b>{qty(b.bought)} lot</b>
                  <span className="ozet-alt">
                    {b.bought > 0 ? `AOF ${fmtPrice(b.buyAvg)}` : '—'}
                  </span>
                </div>
                <div className="ozet-kutu">
                  <span>Satış</span>
                  <b>{qty(b.sold)} lot</b>
                  <span className="ozet-alt">
                    {b.sold > 0 ? `AOF ${fmtPrice(b.sellAvg)}` : '—'}
                  </span>
                </div>
                <div className="ozet-kutu">
                  <span>Net pozisyon</span>
                  <b>{qty(b.net)} lot</b>
                  <span className="ozet-alt">{b.orderCount} emir verdi</span>
                </div>
                <div className="ozet-kutu">
                  <span>Kuruluş</span>
                  <b>{clock(b.createdAt)}</b>
                  <span className="ozet-alt">son değişiklik {clock(b.updatedAt)}</span>
                </div>
              </div>

              {/* Parametreler katlanır — rapor okurken formül duvarı araya girmesin. */}
              <button
                type="button"
                className="ham-ac"
                onClick={() => setParamAcik((v) => !v)}
                aria-expanded={paramAcik}
              >
                {paramAcik ? '▾' : '▸'} Parametreler ({Object.keys(b.params).length})
              </button>
              {paramAcik && (
                <div className="brapor-params">
                  {Object.entries(b.params).map(([ad, formul]) => (
                    <div className="brapor-param" key={ad}>
                      <span className="brapor-param-ad">{ad}</span>
                      <code>{formul}</code>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-section">
                Ne yaptı? <span className="muted">({rapor.events.length} olay, eskiden yeniye)</span>
              </div>
              <ol className="brapor-akis">
                {rapor.events.map((e) => {
                  const r = rozet(e)
                  const ozet = e.category === 'bot' && e.action === 'decision'
                    ? baglamOzeti(e.payload)
                    : null
                  return (
                    <li className="brapor-satir" key={e.id}>
                      <span className="brapor-saat">{clock(new Date(e.ts).getTime() / 1000)}</span>
                      <span className={`brapor-rozet ${r.ton}`}>{r.ad}</span>
                      <span className="brapor-govde">
                        <span className="brapor-mesaj">{e.message ?? `${e.category}/${e.action}`}</span>
                        {ozet && <span className="brapor-baglam">{ozet}</span>}
                        {/* Ham yük: derinlik seviyeleri, değerlendirilen
                            parametreler, emir kimlikleri — analizin dip notu. */}
                        <details className="brapor-ham">
                          <summary>ayrıntı</summary>
                          <code>{JSON.stringify(e.payload, null, 1)}</code>
                        </details>
                      </span>
                    </li>
                  )
                })}
                {rapor.events.length === 0 && (
                  <div className="shadow-empty">Henüz olay yok.</div>
                )}
              </ol>

              <div className="modal-section">
                Eşleşmeleri <span className="muted">({rapor.fills.length})</span>
              </div>
              {rapor.fills.length === 0 ? (
                <div className="shadow-empty">Henüz dolum yok.</div>
              ) : (
                <table className="ledger-table brapor-fills">
                  <thead>
                    <tr>
                      <th>Zaman</th>
                      <th>Yön</th>
                      <th>Tip</th>
                      <th className="num">Miktar</th>
                      <th className="num">Fiyat</th>
                      <th className="num">İşlem</th>
                      <th className="num" title="Dolum anındaki tahta — başka yerde saklanmıyor">
                        Tahta (A/S)
                      </th>
                      <th className="num">PTF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapor.fills.map((f, i) => (
                      <tr key={`${f.order_id}-${f.trade_id}-${i}`}>
                        <td className="muted">{clock(new Date(f.ts).getTime() / 1000)}</td>
                        <td className={f.side === 'BUY' ? 'yon-al' : 'yon-sat'}>
                          {f.side === 'BUY' ? '▲ Alış' : '▼ Satış'}
                        </td>
                        <td>
                          <span className={`fill-tag ${f.liquidity ?? 'trade'}`}>
                            {dolumEtiketi(f.liquidity ?? undefined)}
                          </span>
                        </td>
                        <td className="num">{qty(f.quantity)}</td>
                        <td className="num">{fmtPrice(f.price)}</td>
                        <td className="num muted">{fmtPrice(f.trade_price)}</td>
                        <td className="num muted">
                          {f.best_bid == null && f.best_ask == null
                            ? '–'
                            : `${fmtPrice(f.best_bid)} / ${fmtPrice(f.best_ask)}`}
                        </td>
                        <td className="num muted">{fmtPrice(f.mcp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
